import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Phone, Video, Send, Lock, Languages, ShieldAlert } from 'lucide-react';
import { Avatar, Verified, formatExpiring, useNow } from './shared';
import { conversations } from '../mock';
import { useSafety, ROLES } from './SafetyContext';

function RoleToggle({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-[#8b95a5]">{label}</span>
      <div className="flex bg-[#0a0f18] rounded-full p-0.5">
        {[ROLES.ADULT, ROLES.MINOR].map((r) => (
          <button
            key={r}
            onClick={() => onChange(r)}
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize transition-colors ${
              value === r ? 'bg-[#2f8bff] text-white' : 'text-[#8b95a5]'
            }`}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}

function SafetyDemoBar({ peerRole, setPeerRole }) {
  const { myRole, setMyRole, violations } = useSafety();
  return (
    <div className="bg-[#0c1420] border-b border-[#141c28] px-4 py-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <ShieldAlert size={13} className="text-[#f5a524]" />
        <span className="text-[11px] font-semibold text-[#f5a524]">Safety Demo</span>
        <span className="text-[10px] text-[#5c6675] ml-auto">Violations: {violations}/5</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <RoleToggle label="You" value={myRole} onChange={setMyRole} />
        <span className="text-[#5c6675]">⇄</span>
        <RoleToggle label="Peer" value={peerRole} onChange={setPeerRole} />
      </div>
      <p className="text-[10px] text-[#5c6675] mt-1.5 leading-tight">
        Try sending an emoji or “meet me”. Adult→Minor = instant disable. Minor→Minor = 5 chances w/ fine + ID.
      </p>
    </div>
  );
}

function Bubble({ m, now }) {
  const mine = m.from === 'me';
  if (m.system) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-[11px] text-[#5c6675] bg-[#0c1420] px-3 py-1 rounded-full">{m.text}</span>
      </div>
    );
  }
  return (
    <div className={`flex mb-3 ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[76%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
        <div
          className={`px-3.5 py-2.5 text-[14px] leading-snug ${
            mine
              ? 'bg-[#2f8bff] text-white rounded-2xl rounded-br-md'
              : 'bg-[#161d29] text-[#e7ecf3] rounded-2xl rounded-bl-md'
          }`}
        >
          {m.text}
          {m.photo && <img src={m.photo} alt="" className="mt-2 rounded-xl w-full max-w-[220px] object-cover" />}
        </div>
        <div className="flex items-center gap-1.5 mt-1 px-1">
          {m.translated && (
            <span className="flex items-center gap-0.5 text-[10px] text-[#8b95a5]"><Languages size={10} /> Translated</span>
          )}
          <span className="text-[10px] text-[#5c6675]">{formatExpiring(m.expiresAt, now)}</span>
        </div>
      </div>
    </div>
  );
}

export default function ChatConversation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const now = useNow();
  const convo = conversations.find((c) => c.id === id) || conversations[0];
  const { evaluate } = useSafety();
  const [peerRole, setPeerRole] = useState(ROLES.MINOR);
  const [msgs, setMsgs] = useState(convo.messages);
  const [text, setText] = useState('');
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    const res = evaluate(t, peerRole);
    if (!res.allowed) {
      setText('');
      return; // overlay (lock/disable) is shown at App level
    }
    setMsgs((prev) => [...prev, { id: `n${Date.now()}`, from: 'me', text: t, expiresAt: Date.now() + 24 * 3600000 }]);
    setText('');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-3 py-3 border-b border-[#141c28] bg-[#0a0f18]">
        <button onClick={() => navigate('/chats')} className="p-1 -ml-1 active:scale-90 transition-transform">
          <ChevronLeft size={24} className="text-white" />
        </button>
        <Avatar src={convo.avatar} size={38} online={convo.online} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="text-[15px] font-semibold text-white truncate">{convo.name}</span>
            {convo.verified && <Verified size={13} />}
          </div>
          <span className="text-[11px] text-[#8b95a5]">Expires in 24h</span>
        </div>
        <button className="p-2 active:scale-90 transition-transform"><Phone size={19} className="text-[#2f8bff]" /></button>
        <button className="p-2 active:scale-90 transition-transform"><Video size={20} className="text-[#2f8bff]" /></button>
      </div>

      <SafetyDemoBar peerRole={peerRole} setPeerRole={setPeerRole} />

      <div className="flex-1 overflow-y-auto app-scroll px-4 py-4">
        {msgs.map((m) => <Bubble key={m.id} m={m} now={now} />)}
        {convo.typing && (
          <div className="flex justify-start mb-3">
            <div className="bg-[#161d29] rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8b95a5] typing-dot" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#8b95a5] typing-dot" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#8b95a5] typing-dot" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="px-3 py-3 border-t border-[#141c28] bg-[#0a0f18]">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-[#0f1622] rounded-full px-4 py-2.5 ring-1 ring-[#1c2634]">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Message will disappear in 24h..."
              className="flex-1 bg-transparent text-[14px] text-white placeholder:text-[#5c6675] outline-none"
            />
            <Lock size={15} className="text-[#f5a524]" />
          </div>
          <button onClick={send} className="w-11 h-11 rounded-full bg-[#2f8bff] flex items-center justify-center active:scale-90 transition-transform shrink-0">
            <Send size={18} className="text-white" />
          </button>
        </div>
        <p className="text-center text-[11px] text-[#5c6675] mt-2">Messages disappear after 24 hours.</p>
      </div>
    </div>
  );
}
